import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LoginPage from './user/LoginPage';
import RegistrationPage from './user/RegistrationPage';
import Dashboard from './user/Dashboard';
import EmailVerification from './user/EmailVerification';
import ForgotPassword from './user/ForgotPassword';
import ResetPassword from './user/ResetPassword';
import UserProfile from './user/UserProfile';
import BusinessProfileForm from './user/BusinessProfileForm';
import BusinessProfileRouter from './components/BusinessProfileRouter';
import ContentGenerator from './user/ContentGenerator';
import GenerationHistory from './user/GenerationHistory';
import PlanManagement from './user/PlanManagement';
import BillingPage from './user/BillingPage';
import PaymentSuccess from './user/PaymentSuccess';
import PaymentCancel from './user/PaymentCancel';
import UserManagement from './admin/UserManagement';
import AdminDashboard from './admin/AdminDashboard';
import MainLayout from './user/MainLayout';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { useAuth } from './hooks/useAuth';
import { useLanguage } from './hooks/useLanguage';
import theme from './styles/theme/theme';
import { ErrorBoundary } from './components/ErrorBoundary';
import { setRouter } from './lib/navigation';
import { protectedLoader, adminLoader, agencyLoader } from './lib/routeLoaders';

// Create router configuration
const router = createBrowserRouter([
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/register",
    element: <RegistrationPage />,
  },
  {
    path: "/verify-email",
    element: <EmailVerification />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
  },
  {
    path: "/dashboard",
    loader: protectedLoader,
    element: <ProtectedRoute><DashboardRoute /></ProtectedRoute>,
  },
  {
    path: "/profile",
    loader: protectedLoader,
    element: <ProtectedRoute><UserProfile /></ProtectedRoute>,
  },
  {
    path: "/business-profiles",
    loader: protectedLoader,
    element: <ProtectedRoute><BusinessProfileRouter /></ProtectedRoute>,
  },
  {
    path: "/business-profiles/new",
    loader: protectedLoader,
    element: <ProtectedRoute><BusinessProfileForm /></ProtectedRoute>,
  },
  {
    path: "/business-profiles/:id",
    loader: protectedLoader,
    element: <ProtectedRoute><BusinessProfileForm /></ProtectedRoute>,
  },
  {
    path: "/generate",
    loader: protectedLoader,
    element: <ProtectedRoute><ContentGenerator /></ProtectedRoute>,
  },
  {
    path: "/history",
    loader: protectedLoader,
    element: <ProtectedRoute><GenerationHistory /></ProtectedRoute>,
  },
  {
    path: "/billing",
    loader: protectedLoader,
    element: <ProtectedRoute><BillingPage /></ProtectedRoute>,
  },
  {
    path: "/payment/success",
    loader: protectedLoader,
    element: <ProtectedRoute><PaymentSuccess /></ProtectedRoute>,
  },
  {
    path: "/payment/cancel",
    loader: protectedLoader,
    element: <ProtectedRoute><PaymentCancel /></ProtectedRoute>,
  },
  {
    path: "/plans",
    loader: protectedLoader,
    element: <ProtectedRoute><PlanManagement /></ProtectedRoute>,
  },
  {
    path: "/admin/users",
    loader: adminLoader,
    element: <AdminRoute><UserManagement /></AdminRoute>,
  },
  {
    path: "/",
    element: <Navigate to="/login" replace />,
  },
]);

// Store router instance for global navigation access
setRouter(router);

// Wrapper component that forces re-render when language changes
function AppContent() {
  const { i18n } = useTranslation();
  const { languageKey } = useLanguage();
  
  // The key prop forces React to remount the RouterProvider when language changes
  // This ensures all components get fresh translations
  return (
    <Box key={`app-${i18n.language}-${languageKey}`} sx={{ minHeight: '100vh' }}>
      <RouterProvider router={router} />
    </Box>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <LanguageProvider>
            <AppContent />
          </LanguageProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

// Component to route dashboard based on user role
function DashboardRoute() {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Route admin users to AdminDashboard, others to regular Dashboard
  if (user?.role === 'admin') {
    return <AdminDashboard />;
  }
  
  return <Dashboard />;
}

function ProtectedRoute({ children }) {
  // Loader handles auth check, so we just wrap with MainLayout
  return <MainLayout>{children}</MainLayout>;
}

function AdminRoute({ children }) {
  // Loader handles auth and role check, so we just wrap with MainLayout
  return <MainLayout>{children}</MainLayout>;
}

function AgencyRoute({ children }) {
  // Loader handles auth and role check, so we just wrap with MainLayout
  return <MainLayout>{children}</MainLayout>;
}

export default App;
